import { TWidgetProps } from '@libs/types';
import React, { useEffect, useEffectEvent } from 'react';
import {
    KycErrorType,
    KycOutcome,
    KycProviderName,
    TKycPersonaCredentials,
    TKycProviderData,
    TKycProviderValue,
    TKycSumsubCredentials,
} from '../../../types/data';
import { getInlineStyles } from '../../../utils/styles';
import { PersonaProvider } from './PersonaProvider';
import { SumsubProvider } from './SumsubProvider';
import { useKycOutcome } from './useKycOutcome';

const SUPPORTED_PROVIDERS: KycProviderName[] = [KycProviderName.SUMSUB, KycProviderName.PERSONA];

const isValidProvider = (value?: TKycProviderValue): boolean => {
    if (!value || !value.credentials || !SUPPORTED_PROVIDERS.includes(value.provider)) return false;

    if (value.provider === KycProviderName.SUMSUB) {
        return !!(value.credentials as TKycSumsubCredentials).accessToken;
    }
    const { inquiryId, templateId } = value.credentials as TKycPersonaCredentials;
    return !!inquiryId !== !!templateId;
};

export const KycProvider = ({ widget }: TWidgetProps<TKycProviderData>) => {
    const value = widget.data?.value;
    const valid = isValidProvider(value);
    const { fireOutcome } = useKycOutcome(widget);

    const fireInvalidDataOnMount = useEffectEvent(() => {
        if (!valid) {
            fireOutcome({
                provider: value?.provider ?? null,
                outcome: KycOutcome.ERROR,
                errorType: KycErrorType.INVALID_DATA,
                providerStatus: null,
                inquiryId: null,
            });
        }
    });

    useEffect(() => {
        fireInvalidDataOnMount();
    }, []);

    return (
        <div data-test={widget.uid} style={getInlineStyles({ margin: widget.options?.margin })}>
            {valid && value?.provider === KycProviderName.SUMSUB && <SumsubProvider widget={widget} credentials={value.credentials as TKycSumsubCredentials} />}
            {valid && value?.provider === KycProviderName.PERSONA && (
                <PersonaProvider widget={widget} credentials={value.credentials as TKycPersonaCredentials} />
            )}
        </div>
    );
};
